import { gql } from '@apollo/client';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';
import { ROUTE_FIELDS } from '@/graphql/queries/routes';
import { CUSTOM_SMS } from '@/constants/apiRoutes';
import { post } from '@/utilities/onwardClient';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { SERVICES_FIELDS } from '@/graphql/fragments/services';
import { TRACKING_EVENT_FIELDS } from '@/graphql/fragments/tracking_events';

export const QUERY_ORDERS_BY_OD_OR_PO = gql`
    query GetOrdersByNumber($user_id: uuid!, $order_numbers: [String!] = [], $po_numbers: [String!] = []) {
        orders(
            where: {
                _and: [
                    {
                        _or: [
                            { carrier_id: { _eq: $user_id } }
                            { contractor_id: { _eq: $user_id } }
                            { _and: [{ shipper_id: { _eq: $user_id } }, { carrier_id: { _is_null: true } }] }
                        ]
                    }
                    {
                        order_status: {
                            _nin: [
                                "deleted"
                                "pending_confirmation"
                                "cancelled"
                                "awaiting_payment"
                                "on_hold_awaiting_payment"
                                "processing"
                                "duplicate"
                                "incomplete"
                            ]
                        }
                    }
                    { _or: [{ order_number: { _in: $order_numbers } }, { po_number: { _in: $po_numbers } }] }
                ]
            }
        ) {
            order_id
            order_number
            po_number
        }
    }
`;

export const UPDATE_ORDERS_MANY = gql`
    ${ORDER_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${TRACKING_EVENT_FIELDS}
    mutation BulkUpdateOrders(
        $updates: [orders_updates!] = []
        $events: [order_wh_events_insert_input!] = []
        $tracking_events: [tracking_events_insert_input!] = []
    ) {
        update_orders_many(updates: $updates) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
        insert_order_wh_events(objects: $events) {
            affected_rows
            returning {
                ...WarehouseEvents
            }
        }
        insert_tracking_events(
            objects: $tracking_events
            on_conflict: { constraint: tracking_events_uniq, update_columns: [] }
        ) {
            affected_rows
            returning {
                ...TrackingEventFields
            }
        }
    }
`;

export const QUERY_ORDERS_WITH_DRIVERS_BY_CARRIER_ID_OR_SHIPPER_ID = gql`
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}
    ${ROUTE_FIELDS}
    ${SERVICES_FIELDS}
    query OrdersOMS($limit: Int!, $offset: Int!, $where: orders_bool_exp!, $order_by: [orders_order_by!]) {
        orders_aggregate(where: $where) {
            aggregate {
                totalCount: count
            }
        }
        orders(limit: $limit, offset: $offset, where: $where, order_by: $order_by) {
            ...OrderFields
            services {
                ...ServicesFields
            }
            routes(order_by: { created_at: desc }) {
                mapping_id
                route_id
                order_id
                type
                created_at
                route {
                    ...RouteFields
                    teammateByDriverId {
                        teammate_id
                        username
                    }
                }
            }
            items @client {
                ...ItemFields
            }
            returns @client {
                ...ItemFields
            }
            itemsByOrderId {
                ...ItemFields
            }
            order_shipper {
                client_id
                business_name
            }
            order_carrier {
                client_id
                business_name
            }
            carrier_invoice {
                carrier_invoice_id
                status
            }
            listing {
                listing_id
                bids {
                    bid_id
                    bid_status
                }
            }
        }
    }
`;

export const QUERY_ACTIONABLE_ORDER_AGGREGATES = gql`
    query ActionableOrderAggregates($where: orders_bool_exp!) {
        orders_aggregate(where: $where) {
            aggregate {
                totalCount: count
            }
        }
    }
`;

export const UPSERT_EVENTS = gql`
    ${TRACKING_EVENT_FIELDS}
    mutation UpsertEvents(
        $events: [order_wh_events_insert_input!]!
        $tracking_events: [tracking_events_insert_input!] = []
    ) {
        insert_order_wh_events(
            objects: $events
            on_conflict: { constraint: order_wh_events_pkey, update_columns: [status, received_date] }
        ) {
            affected_rows
            returning {
                event_id
            }
        }
        insert_tracking_events(
            objects: $tracking_events
            on_conflict: { constraint: tracking_events_uniq, update_columns: [] }
        ) {
            affected_rows
            returning {
                ...TrackingEventFields
            }
        }
    }
`;

export const UPSERT_WH_EVENT = gql`
    mutation UpsertWhEvent(
        $events: [order_wh_events_insert_input!] = []
        $update_cols: [order_wh_events_update_column!]! = []
    ) {
        insert_order_wh_events(
            objects: $events
            on_conflict: { constraint: order_wh_events_pkey, update_columns: $update_cols }
        ) {
            affected_rows
        }
    }
`;

export const SEND_MESSAGES = async ({ message, order_ids }) => {
    return post(CUSTOM_SMS, {
        message,
        order_ids,
    });
};

export const GET_CARRIERS = gql`
    ${CLIENT_FIELDS}

    query GetCarriers($where: orders_bool_exp!) {
        results: orders(distinct_on: [carrier_id], where: $where) {
            order_id
            carrier_id
            order_carrier {
                ...ClientFields
            }
        }
    }
`;

export const QUERY_ORDER_SHIPPERS_FOR_CARRIER = gql`
    query GetOrderShippersForCarrier($user_id: uuid!) {
        orders(
            distinct_on: [shipper_id]
            where: {
                _and: [
                    {
                        _or: [
                            { carrier_id: { _eq: $user_id } }
                            { middle_mile_carrier_id: { _eq: $user_id } }
                            { warehouse_carrier_id: { _eq: $user_id } }
                            { contractor_id: { _eq: $user_id } }
                        ]
                    }
                    { shipper_id: { _is_null: false } }
                ]
            }
        ) {
            order_id
            shipper_id
            order_shipper {
                client_id
                business_name
            }
        }
    }
`;
